import { useHttpClient, useToast } from '@airship-platform/airship-patterns';
import { useCallback, useState } from 'react';
import { User } from '../types/models/organization.model';
import { APIResponse } from '../types/services/api-service.type';

type UpdatePayload = Pick<
  User,
  'firstName' | 'lastName' | 'phone' | 'title'
> & {
  smsOptInConsent: boolean;
};

interface UseUserManagement {
  user: User | null;
  isLoading: boolean;
  isFetching: boolean;
  updateUser: (data: UpdatePayload) => Promise<APIResponse<User>>;
  fetchUser: () => Promise<APIResponse<User>>;
  uploadProfileAvatar: (userId: string, file: File) => Promise<APIResponse>;
}

const useUserManagement = (): UseUserManagement => {
  const httpClient = useHttpClient();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  const { showToast } = useToast();
  const fetchUser = useCallback(async () => {
    setIsFetching(true);
    try {
      const user = await httpClient.get<User>(`/users/me`);
      setUser(user);
      return { success: true };
    } catch (error) {
      showToast(`Error fetching user: ${error}`, 'error');
      return { success: false };
    } finally {
      setIsFetching(false);
    }
  }, [httpClient, showToast]);

  const updateUser = useCallback(
    async (data: UpdatePayload) => {
      setIsLoading(true);
      try {
        await httpClient.put<User>(`/users/me`, data);
        return { success: true };
      } catch (error) {
        showToast(`Error updating user: ${error}`, 'error');
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    [httpClient, showToast]
  );

  const uploadProfileAvatar = useCallback(
    async (userId: string, file: File): Promise<APIResponse> => {
      setIsLoading(true);
      try {
        const metadataResponse = await httpClient.post<{
          preSignedUrl: string;
          filekeyId: string;
          fileKey: string;
        }>(`/users/${userId}/avatar`, {
          filename: file.name,
          contentType: file.type,
          uploadType: 'PROFILE_PHOTO',
        });
        await fetch(metadataResponse.preSignedUrl, {
          body: file,
          method: 'PUT',
        });
        return { success: true };
      } catch (error) {
        showToast(`Error uploading profile avatar: ${error}`, 'error');
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    [httpClient, showToast]
  );

  return {
    user,
    isLoading,
    isFetching,
    updateUser,
    fetchUser,
    uploadProfileAvatar,
  };
};

export default useUserManagement;
